import axios from 'axios';

// Mapping activity codes to human-readable labels
const activityMap = {
  3: "Walking",
  8: "Running",
  7: "Biking",
  9: "Unknown",
  // Add more Google Fit activity codes if needed
};

// Fetch activity data from Google Fit API
export const fetchGoogleFitActivity = async (accessToken, startDate, endDate) => {
  try {
    const response = await axios.post(
      'https://www.googleapis.com/fitness/v1/users/me/dataset:aggregate',
      {
        aggregateBy: [{
          dataTypeName: "com.google.activity.segment",
        }],
        bucketByTime: { durationMillis: 86400000 }, // Bucket data daily
        startTimeMillis: startDate.getTime(),
        endTimeMillis: endDate.getTime(),
      },
      {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      }
    );

    const activityData = response.data?.bucket ?? [];  // Default to empty array if no data
    const activitySummary = activityData.map(bucket => {
      const dataset = bucket.dataset ?? [];
      let activitySegments = [];
      dataset.forEach(data => {
        const points = data.point ?? [];
        points.forEach(point => {
          const activityType = point.value[0]?.intVal;  // Activity code
          const activityDuration = (point.endTimeNanos - point.startTimeNanos) / 1000000;  // Duration in milliseconds

          if (activityType && activityMap[activityType]) {
            activitySegments.push({
              type: activityMap[activityType],  // Map activity code to human-readable label
              duration: activityDuration / 1000 / 60,  // Convert to minutes
            });
          }
        });
      });
      return activitySegments;
    });

    // Flatten the array of arrays into one array of activity segments
    const flattenedActivitySummary = activitySummary.flat();
    return flattenedActivitySummary;
  } catch (error) {
    console.error('Error fetching Google Fit activity data:', error.response?.data || error.message);
    return [];
  }
};

// Fetch steps from Google Fit API
export const fetchGoogleFitSteps = async (accessToken, startDate, endDate) => {
  try {
    const response = await axios.post(
      'https://www.googleapis.com/fitness/v1/users/me/dataset:aggregate',
      {
        aggregateBy: [{
          dataTypeName: "com.google.step_count.delta",
        }],
        bucketByTime: { durationMillis: 86400000 },
        startTimeMillis: startDate.getTime(),
        endTimeMillis: endDate.getTime(),
      },
      {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      }
    );

    const stepsData = response.data?.bucket ?? [];  // Default to an empty array if bucket is undefined

    const totalSteps = stepsData.map(bucket => {
      const dataset = bucket.dataset ?? [];
      if (dataset.length > 0) {
        const point = dataset[0].point ?? [];
        if (point.length > 0 && point[0]?.value?.[0]?.intVal) {
          return point[0].value[0].intVal;  // Return the step count
        }
      }
      return 0;  // Default to 0 if no valid data is found
    });
    return totalSteps;
  } catch (error) {
    console.error('Error fetching Google Fit steps:', error);
    return [];
  }
};

export const fetchGoogleFitAllDataForMonth = async (googleAccessToken, startDate, endDate) => {
  try {
    const [steps, calories, weight, sleep, nutrition, activity] = await Promise.all([
      fetchGoogleFitSteps(googleAccessToken, startDate, endDate),
      fetchGoogleFitCalories(googleAccessToken, startDate, endDate),
      fetchGoogleFitWeight(googleAccessToken, startDate, endDate),
      fetchGoogleFitSleep(googleAccessToken, startDate, endDate),
      fetchGoogleNutrition(googleAccessToken, startDate, endDate),
      fetchGoogleFitActivity(googleAccessToken, startDate, endDate)
    ]);

    return { steps, calories, weight, sleep, nutrition, activity };
  } catch (error) {
    console.error("Error fetching Google Fit data", error);
    return { 
      steps: [], 
      calories: [], 
      weight: [], 
      sleep: [], 
      nutrition: [],
      activity: []
    };
  }
};

// Fetch calories from Google Fit API
export const fetchGoogleFitCalories = async (accessToken, startDate, endDate) => {
  try {
    const response = await axios.post(
      'https://www.googleapis.com/fitness/v1/users/me/dataset:aggregate',
      {
        aggregateBy: [{
          dataTypeName: "com.google.calories.expended",
        }],
        bucketByTime: { durationMillis: 86400000 },
        startTimeMillis: startDate.getTime(),
        endTimeMillis: endDate.getTime(),
      },
      {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      }
    );

    const caloriesData = response.data?.bucket ?? [];  // Default to an empty array if bucket is undefined
    const totalCalories = caloriesData.map(bucket => {
      const dataset = bucket.dataset ?? [];
      if (dataset.length > 0) {
        const point = dataset[0].point ?? [];
        if (point.length > 0 && point[0]?.value?.[0]?.fpVal) {
          return point[0].value[0].fpVal;  // Return the calorie count
        }
      }
      return 0;  // Default to 0 if no valid data is found
    });

    return totalCalories;
  } catch (error) {
    console.error('Error fetching Google Fit calories:', error);
    return [];
  }
};

export const fetchGoogleFitWeight = async (accessToken, startDate, endDate) => {
  try {
    const response = await axios.post(
      'https://www.googleapis.com/fitness/v1/users/me/dataset:aggregate',
      {
        aggregateBy: [{
          dataTypeName: "com.google.weight",
        }],
        bucketByTime: { durationMillis: 86400000 },
        startTimeMillis: startDate.getTime(),
        endTimeMillis: endDate.getTime(),
      },
      {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      }
    );

    const weightData = response.data?.bucket ?? [];
    const latestWeight = weightData.map(bucket => {
      const dataset = bucket.dataset ?? [];
      if (dataset.length > 0) {
        const point = dataset[0].point ?? [];
        if (point.length > 0 && point[0]?.value?.[0]?.fpVal) {
          return point[0].value[0].fpVal;  // Return the weight value
        }
      }
      return 0;  // Default to 0 if no valid data is found
    });

    return latestWeight;
  } catch (error) {
    console.error('Error fetching Google Fit weight:', error);
    return [];
  }
};

export const fetchGoogleFitSleep = async (accessToken, startDate, endDate) => {
  try {
    const response = await axios.post(
      'https://www.googleapis.com/fitness/v1/users/me/dataset:aggregate',
      {
        aggregateBy: [{
          dataTypeName: "com.google.sleep.segment",
        }],
        bucketByTime: { durationMillis: 86400000 },
        startTimeMillis: startDate.getTime(),
        endTimeMillis: endDate.getTime(),
      },
      {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      }
    );

    const sleepData = response.data?.bucket ?? [];
    const totalSleep = sleepData.map(bucket => {
      const dataset = bucket.dataset ?? [];
      if (dataset.length > 0) {
        const point = dataset[0].point ?? [];
        if (point.length > 0 && point[0]?.value?.[0]?.intVal) {
          return point[0].value[0].intVal;  // Return the sleep duration
        }
      }
      return 0;  // Default to 0 if no valid data is found
    });

    return totalSleep;
  } catch (error) {
    console.error('Error fetching Google Fit sleep data:', error);
    return [];
  }
};

export const fetchGoogleNutrition = async (accessToken, startDate, endDate) => {
  try {
    const response = await axios.post(
      'https://www.googleapis.com/fitness/v1/users/me/dataset:aggregate',
      {
        aggregateBy: [{
          dataTypeName: "com.google.nutrition",
        }],
        bucketByTime: { durationMillis: 86400000 },
        startTimeMillis: startDate.getTime(),
        endTimeMillis: endDate.getTime(),
      },
      {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      }
    );

    const nutritionData = response.data?.bucket ?? [];
    const totalNutrition = nutritionData.map(bucket => {
      const dataset = bucket.dataset ?? [];
      if (dataset.length > 0) {
        const point = dataset[0].point ?? [];
        if (point.length > 0 && point[0]?.value?.[0]?.fpVal) {
          return point[0].value[0].fpVal;  // Return the nutrition value
        }
      }
      return 0;  // Default to 0 if no valid data is found
    });

    return totalNutrition;
  } catch (error) {
    console.error('Error fetching Google Fit nutrition data:', error);
    return [];
  }
};

// Enhanced mock data generator with more realistic values
export const fetchWalletData = async (startDate, endDate) => {
  try {
    const totalDays = Math.floor((endDate - startDate) / (1000 * 60 * 60 * 24));
    
    // Generate more realistic mock data arrays for the month
    const mockData = {
      steps: Array.from({ length: totalDays }, () => Math.floor(Math.random() * 5000) + 3000), // 3000-8000 steps
      calories: Array.from({ length: totalDays }, () => Math.floor(Math.random() * 300) + 1800), // 1800-2100 calories
      weight: Array.from({ length: totalDays }, () => (Math.random() * 2 + 70).toFixed(1)), // 70-72 kg with 1 decimal
      sleep: Array.from({ length: totalDays }, () => Math.floor(Math.random() * 2) + 7), // 7-9 hours
      nutrition: Array.from({ length: totalDays }, () => Math.floor(Math.random() * 500) + 1800), // 1800-2300 calories
      activity: Array.from({ length: totalDays }, () => {
        const activities = [
          {
            type: 'Walking',
            duration: Math.floor(Math.random() * 30) + 30 // 30-60 minutes
          },
          {
            type: 'Running',
            duration: Math.floor(Math.random() * 20) + 20 // 20-40 minutes
          },
          {
            type: 'Biking',
            duration: Math.floor(Math.random() * 40) + 20 // 20-60 minutes
          }
        ];
        return activities[Math.floor(Math.random() * activities.length)];
      })
    };

    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 500));

    return mockData;
  } catch (error) {
    console.error("Error generating wallet mock data:", error);
    const defaultDays = Math.floor((endDate - startDate) / (1000 * 60 * 60 * 24)) || 30;
    return {
      steps: Array(defaultDays).fill(0),
      calories: Array(defaultDays).fill(0),
      weight: Array(defaultDays).fill(0),
      sleep: Array(defaultDays).fill(0),
      nutrition: Array(defaultDays).fill(0),
      activity: Array(defaultDays).fill({ type: 'Unknown', duration: 0 })
    };
  }
};

// Helper function to get default data when API fails
export const getDefaultData = (totalDays) => ({
  steps: Array(totalDays).fill(5000), // Default 5000 steps
  calories: Array(totalDays).fill(2000), // Default 2000 calories
  weight: Array(totalDays).fill(70.0), // Default 70kg
  sleep: Array(totalDays).fill(8), // Default 8 hours
  nutrition: Array(totalDays).fill(2000), // Default 2000 calories
  activity: Array(totalDays).fill({
    type: 'Walking',
    duration: 30 // Default 30 minutes
  })
});

// Function to check if data is empty or all zeros
export const isDataEmpty = (data) => {
  if (!data) return true;
  return Object.values(data).every(arr => 
    Array.isArray(arr) && arr.every(val => 
      val === 0 || 
      (typeof val === 'object' && val.duration === 0)
    )
  );
};

// Function to get either mock or default data
export const getFallbackData = async (startDate, endDate, useMockData = true) => {
  if (useMockData) {
    return await fetchWalletData(startDate, endDate);
  } else {
    const totalDays = Math.floor((endDate - startDate) / (1000 * 60 * 60 * 24));
    return getDefaultData(totalDays);
  }
};
